<template>
  <div class="sample-injection">
    <InjectionHeaderComponent
      :loading="updated !== true"
      :injection="{ substate: null, ...injection }"
      :injectionSocket="injectionSocket"
      :sampleSocket="sampleSocket"
      :sample="sample"
      :method="method"
      :column="column"
      :postprocessings="postprocessings"
      :measurementsSelectedIds="config.measurementsSelectedIds"
    />

    <InjectionErrors :error="injection.error" :method="method" />

    <div class="sample-injection__information">
      <DeviceMethodColumnComponent
        :device="device"
        :configuration="injection.configuration"
        :vial="injection.vial"
        :method="method"
        :methodOverride="injection.method_override"
        :column="column"
        :sample="sample"
        :postprocessings="postprocessings"
        :injectionId="injection.id"
        class="sample-injection__device-method-column"
      />
      <Liquids
        v-if="isInjectionInProgress && device.id"
        :deviceId="device.id"
        :isCromite="device.model === 'cmt'"
        :method="method"
        class="sample-injection__liquids"
      />
    </div>

    <not-available-component v-if="injection.state === 'NEW'" style="margin-top: 32px">
      <div>Did not started</div>
    </not-available-component>
    <template v-else-if="injection.state === 'PEN' || injection.state === 'PRE'">
      <RunSubstate
        v-if="injection.substate != null"
        :substate="injection.substate"
        style="margin: 64px 0 40px 0"
      />
      <nothing-there-component v-else style="margin-top: 32px">
        No recorded data
      </nothing-there-component>
    </template>
    <template v-else>
      <div class="sample-injection__wrapper-panel-control">
        <ChromatogramControl
          v-model="config"
          :measurements="measurementsSelectable"
          :pressure="pressure"
          :isInjectionRunning="injection.state === 'RUN'"
          :hasGradient="hasGradient"
          :hasPostprocessing="hasPostprocessing"
          class="sample-injection__panel-control"
        />
      </div>

      <ChartGradient
        v-if="config.isShowGradient && method.gradient"
        :gradient="method.gradient"
        :injectionMinutes="effectiveRunTimeMin"
        class="sample-injection__chart-gradient"
      />

      <NewChartChromatogram
        v-if="measurementsSelectedFiltered.length && !isLoadingPostprocessings"
        :measurements="measurementsSelectedFiltered"
        :method="method"
        :injection="injection"
        :isShowPressure="config.isShowPressure"
        :measurementPressure="measurementPressure"
        :showPeaksList="!sample.archived && !injection.archived"
        :canRemovePeak="isSequenceCalibrationMode"
        :editable="canEditChromatogram"
        :doPerformanceOptimisations="isInjectionRunning"
        :hasToolbar="!isInjectionRunning && !sample.archived && !injection.archived"
        :timeMax="effectiveRunTimeMin"
        :initialZoom.sync="initialZoom"
        :creationDate="new Date(injection.created)"
        :isApplyPostprocessing="config.isApplyPostprocessing && !isInjectionInProgress"
        :postprocessings="postprocessings"
        :hasZoomActivationOverlay="hasZoomActivationOverlay"
        :hasPostprocessingsToApply="hasPostprocessingsToApply"
        @currentPressure="setCurrentPressure"
        @applyPostprocessing="setIsApplyPostprocessing"
        @zoomActivation="activateZoom"
        @liveUpdate="resetZoom"
      />

      <LoadingComponent v-else-if="updated == null || isLoadingPostprocessings" />
      <NothingThereComponent v-else style="margin-top: 32px">
        No measurement
      </NothingThereComponent>
    </template>
  </div>
</template>

<script>
  import SampleInjectionSocket from 'api/sockets/SampleInjectionSocket';
  import NewChartChromatogram from '@/components/blocks/charts/chromatogramNew/NewChartChromatogram.vue';
  import NothingThereComponent from 'components/element/NothingThereComponent.vue';
  import DeviceMethodColumnComponent from 'components/element/DeviceMethodColumnComponent.vue';
  import LoadingComponent from 'components/element/LoadingComponent.vue';
  import NotAvailableComponent from 'components/element/NotAvailableComponent.vue';
  import InjectionHeaderComponent from 'components/block/InjectionHeaderComponent.vue';
  import { isPrevDateOlder } from 'utils/utils.ts';
  import InjectionErrors from 'components/element/InjectionErrors';
  import ChromatogramControl from '@/uikitProject/chromatogram/panels/vueChromatogramControl/ChromatogramControl';
  import ChartGradient from '@/uikitProject/charts/vueChartGradient/ChartGradient';
  import RunSubstate from '@/uikitProject/states/packs/RunSubstate';
  import Liquids from 'components/blocks/injections/liquids/Liquids';
  import { sendDebugEvent } from 'utils/sentryHelpers';
  import faviconService, { FaviconStatuses } from '@/services/FaviconService';
  import { INJECTION_STATES } from '@/constants/injections/states';
  import { apiMeasurements } from '@/api/graphql/cloud/measurements';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { CHECK_CONNECTION_INTERVAL_MS } from '@/constants/injections/connection';

  const DEBUG_TASK_MEASUREMENT_CAN_BE_UNDEFINED =
    'https://gitlab.com/hplc-cloud-group/hplc-cloud-server/-/issues/1841';

  export default {
    name: 'SampleInjectionComponent',

    components: {
      NewChartChromatogram,
      Liquids,
      RunSubstate,
      ChartGradient,
      ChromatogramControl,
      InjectionErrors,
      NotAvailableComponent,
      LoadingComponent,
      DeviceMethodColumnComponent,
      NothingThereComponent,
      InjectionHeaderComponent,
    },

    props: {
      injectionData: Object,
      sample: {
        type: Object,
        required: true,
      },
      sampleSocket: {
        type: Object,
        required: true,
      },
      canEditChromatogram: {
        type: Boolean,
        default: true,
      },

      isVisible: {
        type: Boolean,
        default: true,
      },
    },

    data() {
      const { id } = this.injectionData.injection;

      return {
        id,

        injection: this.injectionData.injection,
        device: this.injectionData.device,
        method: this.injectionData.method,
        column: this.injectionData.column,
        postprocessings: null,
        updated: null,
        timerConnectionLost: null,

        measurementsSelected: [],

        config: {
          measurementsSelectedIds: [],
          isShowGradient: false,
          isShowPressure: false,
          isApplyPostprocessing: false,
        },

        isLoadingInjection: false,

        injectionSocket: this.createInjectionSocket(id),
        listenersSampleSocketGroupId: null,

        wasInProgressRecently: false,

        initialZoom: null,
        hasZoomActivationOverlay: true,

        currentPressure: null,

        subscriptionForInjection: null,
        subscriptionForPostprocessings: null,
        subscriptionForInjectionProgress: null,

        isLoadingPostprocessings: true,
      };
    },

    computed: {
      configuration() {
        return this.injectionData?.injection?.configuration ?? {};
      },
      measurements() {
        return this.injection.measurements;
      },
      measurementPressure() {
        return Object.values(this.injection.measurements).find(
          (measurement) => measurement?.type === 'PRS',
        );
      },
      measurementsSelectable() {
        return Object.values(this.injection.measurements).reduce((measurements, measurement) => {
          if (!measurement) {
            sendDebugEvent('#1841: Measurement is undefined', [
              ['All measurements', this.injection.measurements],
              ['Task', DEBUG_TASK_MEASUREMENT_CAN_BE_UNDEFINED],
            ]);
          }

          if (measurement && measurement?.type !== 'PRS') {
            measurements[measurement.id] = measurement;
          }
          return measurements;
        }, {});
      },

      isPressureOutOfBounds() {
        if (this.currentPressure != null) {
          const { pressure_min, pressure_max } = this.method;

          const isOutOfMin = pressure_min != null && this.currentPressure < pressure_min;
          const isOutOfMax = pressure_max != null && this.currentPressure > pressure_max;

          return this.isInjectionRunning && (isOutOfMin || isOutOfMax);
        }
        return false;
      },
      pressure() {
        if (this.measurementPressure) {
          return {
            current: this.currentPressure?.toFixed(),
            isOutOfBounds: this.isPressureOutOfBounds,
          };
        }
        return null;
      },
      isInjectionRunning() {
        return this.injection.state === INJECTION_STATES.RUNNING;
      },
      isInjectionInProgress() {
        return ['PEN', 'PRE', 'RUN', 'FIN', 'EXE'].includes(this.injection.state);
      },
      effectiveRunTimeMin() {
        const runTimeMs = this.injection?.method_override?.run_time_ms ?? this.method?.run_time_ms;
        if (runTimeMs != null) {
          return runTimeMs / 1000 / 60;
        }
        return this.method?.run_time_min;
      },

      hasGradient() {
        return (
          this.configuration?.pump?.type === 'TWO SYRINGE GRADIENT' &&
          Boolean(this.method?.gradient?.length)
        );
      },

      hasPostprocessing() {
        return (
          // TODO
          this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) =>
                  measurement.id === postprocessing.measurement.id &&
                  measurement.processed_data_url != null,
              ),
          ) ?? false
        );
      },

      measurementsSelectedFiltered() {
        return this.measurementsSelected.filter(Boolean);
      },

      hasPostprocessingsToApply() {
        return this.sample?.sequence?.postprocessing_id != null || this.postprocessings?.length > 0;
      },

      isSequenceCalibrationMode() {
        return this.sample?.sequence?.is_calibration_mode ?? false;
      },
    },

    watch: {
      'config.measurementsSelectedIds'(ids) {
        setTimeout(() => {
          this.measurementsSelected = ids.map((id) => this.injection.measurements[id]);
        }, 0);
      },
      injectionData(injectionData) {
        if (injectionData == null) throw Error('injectionData should not be null');

        const idChanged = this.id !== injectionData.injection.id;

        const prevDateOlder = isPrevDateOlder(
          injectionData.injection.date_synced,
          this.injection.date_synced,
        );

        if (idChanged) {
          this.hasZoomActivationOverlay = true;
        }

        if (!idChanged && !prevDateOlder) {
          return;
        }

        this.device = injectionData.device;
        this.method = injectionData.method;
        this.column = injectionData.column;
        this.updated = null;

        this.id = injectionData.injection.id;
        const measurementsOld = this.injection.measurements;
        this.injection = injectionData.injection;

        if (idChanged) {
          this.initMeasurementsSelected(measurementsOld);

          this.isLoadingPostprocessings = true;

          this.initInjection(this.id);
          this.injectionSocket = this.createInjectionSocket(this.id);
        }
      },
      isInjectionRunning: {
        handler(value) {
          if (value && this.measurementPressure) {
            this.config.isShowPressure = true;
          }
        },
        immediate: true,
      },
      isInjectionInProgress: {
        handler(value) {
          if (value) {
            faviconService.setStatus(FaviconStatuses.RUNNING);
          }
        },
        immediate: true,
      },
      measurementsSelectedFiltered() {
        if (this.config.isApplyPostprocessing) {
          const hasAtLeastOneMeasurementWithPostprocessing = this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) => measurement.id === postprocessing.measurement.id,
              ),
          );

          if (!hasAtLeastOneMeasurementWithPostprocessing) {
            this.config.isApplyPostprocessing = false;
          }
        }
      },
    },

    created() {
      this.initInjection(this.id);
    },

    beforeDestroy() {
      this.stopSubscriptions();

      if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);
      this.destroyInjectionSocket();

      faviconService.setStatus(FaviconStatuses.NONE);
    },

    methods: {
      initMeasurementsSelected(_measurementsOld) {
        const measurementsNew = Object.values(this.injection.measurements);
        const measurementsOld = Object.values(_measurementsOld);

        const isSelectedAllMeasurements =
          measurementsOld.length === this.measurementsSelected.length;

        const measurementsSelectedTypes = this.measurementsSelected.map(
          (measurement) => measurement?.type,
        );
        const measurementsSelectedBasedOnPrevChoice = measurementsNew.filter((measurement) =>
          measurementsSelectedTypes.includes(measurement?.type),
        );

        if (isSelectedAllMeasurements && measurementsOld.length > 1) {
          this.config.measurementsSelectedIds = measurementsNew.map(({ id }) => id);
          this.measurementsSelected = measurementsNew;
        } else if (measurementsSelectedBasedOnPrevChoice.length) {
          this.config.measurementsSelectedIds = measurementsSelectedBasedOnPrevChoice.map(
            ({ id }) => id,
          );
          this.measurementsSelected = measurementsSelectedBasedOnPrevChoice;
        } else {
          const defaultMeasurementName = this.method?.default_measurement ?? 'UV';
          // Analog detector's name is null. So I need to use its type field
          const defaultMeasurementType =
            this.method?.default_measurement === 'Analog signal' && 'AD';

          const defaultMeasurement =
            measurementsNew.find((measurement) => {
              return (
                measurement.name === defaultMeasurementName ||
                measurement.type.startsWith(defaultMeasurementType)
              );
            }) ?? this.injection.measurements[this.injection.primary_measurement_id];

          if (defaultMeasurement) {
            this.config.measurementsSelectedIds = [defaultMeasurement.id];
            this.measurementsSelected = [defaultMeasurement];
          }
        }
      },

      createInjectionSocket(id) {
        this.injectionSocket?.close(this.listenersSampleSocketGroupId);
        return SampleInjectionSocket.start(id);
      },
      destroyInjectionSocket() {
        if (this.injectionSocket != null) {
          this.injectionSocket?.close(this.listenersSampleSocketGroupId);
        }
      },

      onEvent() {
        if (this.timerConnectionLost != null) {
          clearTimeout(this.timerConnectionLost);
        }

        if (this.isInjectionInProgress) {
          this.wasInProgressRecently = true;
          this.timerConnectionLost = setTimeout(
            () => this.setNoInternetState(),
            CHECK_CONNECTION_INTERVAL_MS,
          );
        }

        if (
          this.injection.state === INJECTION_STATES.FAILED ||
          this.injection.state === INJECTION_STATES.READY
        ) {
          if (this.wasInProgressRecently) {
            faviconService.setStatus(
              this.injection.state === INJECTION_STATES.FAILED
                ? FaviconStatuses.ERROR
                : FaviconStatuses.COMPLETED,
            );
          }
        }
      },

      setNoInternetState() {
        this.injection.state = '@';
      },

      onInjection(injection) {
        if (this.injectionData.injection.id !== injection.id) {
          return;
        }

        const measurementsOld = injection.measurements;

        this.injection = {
          ...this.injection,
          ...injection,
        };
        // eslint-disable-next-line vue/no-mutating-props
        this.injectionData.injection = this.injection;
        this.initMeasurementsSelected(measurementsOld);

        const hasPostprocessedSelected =
          this.postprocessings?.some(
            (postprocessing) =>
              postprocessing.measurement.processing_state !== 'error' &&
              this.measurementsSelectedFiltered.some(
                (measurement) =>
                  measurement.id === postprocessing.measurement.id &&
                  measurement.processed_data_url != null,
              ),
          ) ||
          this.measurementsSelectedFiltered.some(
            (measurement) => measurement?.processed_data_url != null,
          );

        if (hasPostprocessedSelected) {
          this.config.isApplyPostprocessing = true;
        }

        this.updated = true;
      },

      setIsApplyPostprocessing(isApplyPostprocessing) {
        this.config.isApplyPostprocessing = isApplyPostprocessing;
      },

      setCurrentPressure(currentPressure) {
        this.currentPressure = currentPressure;
      },

      resetZoom() {
        this.initialZoom = null;
      },

      activateZoom() {
        this.hasZoomActivationOverlay = false;
      },

      async initInjection(injectionId) {
        this.stopSubscriptions();
        await this.initSubscriptionForInjection(injectionId);
        this.initSubscriptionForInjectionProgress(injectionId);
        this.initSubscriptionForPostprocessings(injectionId);
      },
      stopSubscriptions() {
        this.subscriptionForInjection?.unsubscribe();
        this.subscriptionForPostprocessings?.unsubscribe();
        this.subscriptionForInjectionProgress?.unsubscribe();
      },

      initSubscriptionForInjection(injectionId) {
        const { promise, resolve } = Promise.withResolvers();
        this.subscriptionForInjection = apiInjections.subscribeForInjection(
          injectionId,
          (injection) => {
            console.warn('event injection', injection);
            this.onInjection(injection);
            this.onEvent();
            resolve();
          },
        );

        return promise;
      },

      async initSubscriptionForPostprocessings(injectionId) {
        this.isLoadingPostprocessings = true;

        try {
          const { promise, resolve } = Promise.withResolvers();

          this.subscriptionForPostprocessings = apiMeasurements.subscribeForPostprocessings(
            injectionId,
            (postprocessings) => {
              this.postprocessings = postprocessings;
              this.config.isApplyPostprocessing = this.postprocessings?.some(
                (postprocessing) =>
                  postprocessing.measurement.processing_state !== 'error' &&
                  this.measurementsSelectedFiltered.some(
                    (measurement) =>
                      measurement.id === postprocessing.measurement.id &&
                      measurement.processed_data_url != null,
                  ),
              );

              resolve();
            },
          );

          await promise;
          await this.$nextTick();
        } finally {
          this.isLoadingPostprocessings = false;
        }
      },

      initSubscriptionForInjectionProgress(injectionId) {
        this.subscriptionForInjectionProgress = apiInjections.subscribeForInjectionProgress(
          injectionId,
          (injectionProgress) => {
            console.warn('event injectionProgress', injectionProgress);

            if (this.injectionData.injection.id !== injectionProgress.id) {
              return;
            }

            this.injection.state = injectionProgress.state;
            this.injection.substate = injectionProgress.substate;
            this.injection.progress = injectionProgress.progress;
            this.injection.progress_time_str = injectionProgress.progress_time_str;
            this.injection.date_synced = injectionProgress.date_synced;
            this.injection.elapsed_time_ms = injectionProgress.elapsed_time_ms;
            this.injection.expected_duration_ms = injectionProgress.expected_duration_ms;

            // eslint-disable-next-line vue/no-mutating-props
            this.injectionData.injection = this.injection;

            this.onEvent();
          },
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sample-injection {
    &__information {
      display: flex;
      flex-direction: column;
      margin-top: 24px;
      margin-bottom: -20px;
      padding: 0 32px;

      @media (max-width: $screen-sm-max) {
        padding: 0 16px;
      }
    }

    &__device-method-column {
      margin-right: 40px;
      margin-bottom: 20px;
    }

    &__liquids {
      min-width: 280px;
      margin-bottom: 20px;
    }

    &__wrapper-panel-control {
      display: flex;
      justify-content: center;
      margin: 48px 0 8px;

      @media (max-width: $screen-xs-max) {
        padding-top: 16px;
        overflow-x: auto;
        justify-content: flex-start;

        &::before,
        &::after {
          content: '';
          display: block;
          width: 16px;
          flex: none;
        }
      }
    }

    &__panel-control {
      margin: auto auto 24px;
    }

    &__chart-gradient {
      padding: 0 32px;
      margin-bottom: 20px;

      @media (max-width: $screen-sm-max) {
        padding: 0 10px;
      }
    }
  }
</style>
